<template>
  <div class="smart-home">
    <Breadcrumb :items="breadcrumb" />
    <div class="smart-home__stage">
      <CdnImage
        class="smart-home__stage-background"
        src="/content/content-images/landingpages/smart-home/31282e7148ec1e15eac8b7291b834751/buehne-smarthome-s-02-2025@2x.jpg"
        src-sm="/content/content-images/landingpages/smart-home/68db90ba3538a3a895503a59cde9bd27/buehne-smarthome-m-02-2025@2x.jpg"
        src-md="/content/content-images/landingpages/smart-home/bf2b8d52435302ac3d238e42e738f210/buehne-smarthome-l-02-2025-v2@2x.jpg"
        alt=""
        sizes="xs:100vw sm:2048px md:3842px"
      />
      <section class="smart-home__stage-container">
        <GridContainer padding>
          <div class="smart-home__stage-content">
            <Heading
              tag="h1"
              font-weight="ultra"
              text-uppercase
              spaces-bottom
              class="smart-home__title"
              text="Smart Home"
            />
            <Heading
              tag="h2"
              size="lg"
              font-weight="ultra"
              text-uppercase
              spaces-bottom
              class="smart-home__subtitle"
            >
              Jetzt Gerätevielfalt entdecken!
            </Heading>
            <Paragraph class="smart-home__stage-copy">
              Erfahre, wie Smart Home dein Leben leichter macht. Mach dein Zuhause einfach smart,
              mit vielen Geräten, die du sofort per App steuern kannst.
            </Paragraph>
            <div class="smart-home__stage-buttons-wrapper">
              <LinkButton
                v-for="button in content.stage.buttons"
                :key="button.label"
                :tag="button.external ? 'a' : 'nuxt-link'"
                :to="button.target"
                :is-external="button.external"
                outline
                class="smart-home__stage-button"
                >{{ button.label }}</LinkButton
              >
            </div>
          </div>
          <div class="smart-home__stage-hero">
            <CdnImage
              class="smart-home__stage-hero-image"
              src="/content/content-images/landingpages/smart-home/b815375805b14e8ebfc14e7f33817b8d/produkte-endgeraete-buehne-02-2025-v2@2x.png"
              alt="Smart Home Geräte"
              sizes="xs:100vw sm:100vw md:610px lg:765px"
            />
          </div>
        </GridContainer>
      </section>
    </div>
    <GridContainer padding>
      <BrandsShowcase
        v-if="smartHomeBrands"
        :custom-speed="'30s'"
        :brands="smartHomeBrands"
      />
      <div
        class="smart-home__mza-app"
        :class="{ 'smart-home__mza-app--customer': isTelekomCustomer }"
      >
        <div class="smart-home__mza-app-content">
          <h2 class="smart-home__mza-app-label">
            <SvgIcon :image="mzaIcon" />
            MagentaZuhause App
          </h2>
          <Heading
            tag="h3"
            size="md"
            font-weight="ultra"
            text-uppercase
            class="smart-home__mza-app-heading"
          >
            Eine App für smarte Gerätevielfalt
          </Heading>
          <Paragraph>
            Die MagentaZuhause App bietet dir eine umfassende Lösung, um dein Zuhause intelligent zu
            vernetzen und deinen Alltag effizienter zu gestalten. Alles, was du brauchst, vereint an
            einem Ort – für mehr Komfort und Bequemlichkeit in deinem smarten Zuhause. Als Telekom
            Festnetz-Kunde kannst du die App kostenlos laden und dich mit deinem Telekom Login
            anmelden.
          </Paragraph>

          <Toggle
            v-model="isTelekomCustomer"
            label="Ich bin bereits Telekom Festnetz-Kunde"
            class="grid__item"
          />
          <template v-if="isTelekomCustomer">
            <div
              class="smart-home__mza-app-downloads-copy-wrapper"
              :class="{ 'smart-home__mza-app-downloads-copy-wrapper--desktop': !isMobile }"
            >
              <div class="smart-home__mza-app-downloads-copy">
                <span>Bereits über 2 Mio. Downloads</span><br />
                <span>QR-Code scannen und kostenlos laden</span>
              </div>
              <client-only>
                <DownloadApp tealium-suffix="smart-home-entdecken" />
              </client-only>
            </div>
          </template>
        </div>
        <template v-if="!isTelekomCustomer">
          <LinkButton
            tag="a"
            to="https://www.telekom.de/shop/festnetz"
            outline
            class="smart-home__mza-app-link-new-customer"
            is-external
          >
            Jetzt Festnetz-Kunde werden
          </LinkButton>
        </template>
        <div class="smart-home__mza-app-animation">
          <LottieAnimation
            class="smart-home__mza-app-lottie"
            path="/lottiefiles/smarthome/screens-carousel.json"
          />

          <div class="smart-home__mza-app-awards">
            <CdnImage
              :width="95"
              :height="49"
              render-without-figure
              src="/content/content-images/red-dot-award/a6e3afcc873f02482f5fa7a87bf7207d/award-if-single@2x.png"
              alt="iF Design Award"
            />

            <CdnImage
              :width="84"
              :height="47"
              render-without-figure
              src="/content/content-images/red-dot-award/cc7dfcf25954a1340af0a2a4183e2084/award-red-dot-single@2x.png"
              alt="Reddot Winner 2022"
            />
          </div>
        </div>

        <LinkButton
          tag="a"
          to="https://www.telekom.de/smarte-produkte/smart-home"
          outline
          class="smart-home__mza-app-link"
          is-external
        >
          Erfahre mehr über die App
        </LinkButton>
      </div>

      <SmartHomeCardTiles />

      <BrandsShowcase
        v-if="smartHomeBrands"
        :custom-speed="'30s'"
        :brands="smartHomeBrands"
      />
      <ImageTeaser
        class="smart-home__footer-banner"
        align-v="start"
        lazy
        heading-tag="h2"
        title="Passende Geräte für dein smartes Zuhause finden"
        description="Finde ganz einfach heraus, welche Smart Home Geräte mit welcher App Version der
            MagentaZuhause App gesteuert werden können und ob eine Zentrale, weitere Hardware oder
            Software nötig ist."
        :background="{
          src: '/content/content-images/landingpages/smart-home/5569c9d89c2dd11f940b12337670454d/kompatible-geraete-bg-s@2x.jpg',
          srcSm:
            '/content/content-images/landingpages/smart-home/4b751ac3a1ade641bee6aa6c07e32e02/kompatible-geraete-bg-m@2x.jpg',
          srcMd:
            '/content/content-images/landingpages/smart-home/11f2864a2ed32699a55ead99b2132f29/kompatible-geraete-bg-l@2x.jpg',
          sizes: 'xs:100vw sm:100vw md:100vw lg:1446px',
        }"
        :image="{
          src: '/content/content-images/landingpages/smart-home/e9863da8a28401ef6a13215c3b62a955/visual-kompatible-geraete@2x.png',
          sizes: 'xs:100vw sm:100vw md:50vw lg:723px',
          alt: 'Telekom Speedport in schwarz. Davor ein Handy mit der Magenta Zuhause App',
        }"
        style="--teaser-height-xs: auto; --teaser-height-sm: 550px"
      >
        <template #button>
          <LinkButton
            to="https://www.telekom.de/smarte-produkte/smart-home/kompatible-geraete"
            outline
            tag="a"
            class="smart-home__footer-banner-link"
            is-external
          >
            Kompatible Geräte finden
          </LinkButton>
        </template>
      </ImageTeaser>

      <div class="smart-home__flyout-banner">
        <CdnImage
          :height="64"
          :width="50"
          class="smart-home__flyout-banner-app-screen"
          src="/content/content-images/landingpages/smart-home/f4352e5fd4b6e7d565d1568443acdd4e/mza-icon-smartphone.png"
          alt="Das MagentaZuhause-App-Logo über der Zeichnung eines Telefonbildschirms"
        />

        <Paragraph
          theme="light"
          class="smart-home__flyout-banner-text"
        >
          <span class="smart-home__flyout-banner-text--mobile"
            >App kostenlos laden und ausprobieren!</span
          >
          <span class="smart-home__flyout-banner-text--desktop">
            Jetzt <strong>MagentaZuhause App</strong> kostenlos laden und ausprobieren!
          </span>
        </Paragraph>
        <client-only>
          <DownloadApp
            tealium-suffix="smart-home-entdecken-flyout"
            class="smart-home__flyout-banner-qr"
            :qr-code-size="85"
          />
        </client-only>
      </div>
    </GridContainer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useLazyAsyncData } from '#imports';
import { getHeader } from '@/helpers/page-header-compositon';
import { Track } from '@/helpers/page-tracking';
import Breadcrumb from '@/components/breadcrumb.vue';
import {
  Heading,
  Paragraph,
  LinkButton,
  GridContainer,
  ImageTeaser,
  Toggle,
} from '@i22-td-smarthome/component-library';
import CdnImage from '@/components/cdn-image.vue';
import BrandsShowcase from '@/components/brands-showcase.vue';
import LottieAnimation from '@/components/lottie-animation.vue';
import SmartHomeCardTiles from '@/components/smart-home-card-tiles.vue';
import mzaIcon from '@/assets/images/mza-logo-magenta-solid.svg';
import DownloadApp from '@/components/download-app.vue';
import { isMobile } from '@/lib/detect-device';
import useScrollAnimation from '@/components/anker/animations/scroll-animation';
import type { OfferType, BrandAggregationQuery } from '~/lib/goliath/goliath.types';
import getProductBrands from '~/lib/goliath/brand-aggregation';

Track();
getHeader({
  title: ref('SmartHome entdecken'),
  isIndexed: ref(false),
  description: ref(
    'Mehr Sicherheit & Komfort für das Zuhause: SmartHome ist einfach einzurichten und mit vielen Herstellern kompatibel. Jetzt im Telekom Zubehörshop entdecken!'
  ),
});

const breadcrumb = [{ label: 'Smart Home', to: '/smart-home-entdecken' }];

const isTelekomCustomer = ref(false);

const content = {
  stage: {
    buttons: [
      {
        label: 'Gerätevielfalt entdecken',
        target: '/smart-home',
      },
      {
        label: 'Mehr zu Smart Home',
        target: 'https://www.telekom.de/smarte-produkte/smart-home',
        external: true,
      },
    ],
  },
};

const { data: smartHomeBrands, error: errorDataBrands } = useLazyAsyncData<
  NonNullable<NonNullable<BrandAggregationQuery['offers']>['aggregations']>['brands']
>('smarthome-brands', () =>
  getProductBrands({
    filter: {
      productProviders: [],
      brandSlugs: [],
      colorSlugs: [],
      compatibleProductFamilySlugs: [],
      productTagSlugs: [],
      onetimePrice: {},
      productCategoryPaths: ['smart-home'],
      listed: true,
      types: ['TANGIBLE', 'MIXED'] as OfferType[],
      productAvailable: true,
      hasAlwaysDisplayed: true,
    },
  })
);
// handle errors
if (errorDataBrands.value) {
  console.error(errorDataBrands.value);
}

const { createScrollTrigger, createTweenTo } = useScrollAnimation();

onMounted(() => {
  createScrollTrigger({
    trigger: '.smart-home__mza-app',
    start: 'top+=500 center',
    end: 'top+=800 center',
    scrub: 1,
    animation: createTweenTo('.smart-home__flyout-banner', {
      y: 0,
    }),
  });
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.smart-home {
  &__stage {
    margin-bottom: $grid-base;
    position: relative;

    &-hero {
      @include mq($mq-sm) {
        max-width: 80%;
        margin-left: auto;
        margin-top: -20%;
      }

      @include mq($mq-md) {
        max-width: 70%;
        margin-top: -30%;
      }

      @include mq($mq-lg) {
        max-width: 715px;
        margin-top: -380px;
      }

      @include mq($mq-xl) {
        max-width: 765px;
        margin-top: -330px;
        margin-right: -100px;
      }

      &-image {
        :deep(img) {
          width: 100%;
          aspect-ratio: 640 / 483;
        }
      }
    }

    &-background {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 0;

      :deep(img) {
        object-fit: cover;
        object-position: top;
        width: 100%;
        height: 100%;
      }

      @include mq($mq-md) {
        height: 544px;
      }
    }

    &-container {
      max-width: 1240px;
      margin: 0 auto;
      padding-top: $grid-base;
      z-index: 1;
      position: relative;

      @include mq($mq-sm) {
        padding-top: 2 * $grid-base;
      }

      @include mq($mq-md) {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        padding-top: 7 * $grid-base;
      }
    }

    &-content {
      position: relative;
      margin-bottom: $grid-base;

      @include mq($mq-sm) {
        max-width: 400px;

        p {
          max-width: 295px;
        }
      }

      @include mq($mq-md) {
        max-width: 480px;

        p {
          max-width: 370px;
        }
      }

      @include mq($mq-xl) {
        p {
          @include font-size(21px, 25px);
          max-width: 463px;
        }
      }
    }

    &-copy {
      @include mq(false, $mq-sm - 1) {
        text-align: center;
      }
    }
  }

  &__subtitle,
  &__title {
    @include mq(false, $mq-sm - 1) {
      text-align: center;
    }
  }

  &__title {
    @include font-size(21px, 25px);
    margin-bottom: $grid-base;
    :deep(span) {
      color: var(--color-primary);
    }

    @include mq($mq-md) {
      @include font-size(36px, 36px);
    }
  }

  &__subtitle {
    margin-bottom: $grid-base;

    @include mq($mq-lg) {
      margin-bottom: 2 * $grid-base;
    }
  }

  &__stage-button {
    font-weight: 700;
  }

  &__stage-buttons-wrapper {
    display: flex;
    flex-direction: column;
    width: 260px;
    margin: 0 auto;
    gap: $grid-base;

    @include mq($mq-sm) {
      margin: 0;
    }

    @include mq($mq-lg) {
      margin-top: 100px;
      flex-direction: row;
      width: auto;
    }
  }

  &__mza-app {
    margin-bottom: 7 * $grid-base;

    @include mq($mq-md) {
      display: grid;
      gap: 1rem;
      align-items: flex-start;
      grid-template-areas: 'content animation' 'new-link animation' 'link animation' '. animation';
      grid-template-columns: auto 620px;
      min-height: 620px;
    }

    @include mq($mq-lg) {
      grid-template-areas: 'content content animation' 'new-link link animation' '. . animation';
      grid-template-columns: min-content auto 640px;
      min-height: 552px;
    }

    @include mq($mq-xl) {
      padding-left: 10%;
    }

    &--customer {
      @include mq($mq-md) {
        grid-template-areas: 'content animation' 'link animation' '. animation';
        grid-template-columns: auto 620px;
      }

      @include mq($mq-lg) {
        grid-template-columns: auto 640px;
      }
    }

    &-label {
      @include font-size(21px, 25px);
      font-weight: 800;
      display: flex;
      align-items: center;
      gap: $grid-base;
      margin-bottom: $grid-base;

      .icon {
        width: 3 * $grid-base;
        height: 3 * $grid-base;
      }
    }

    &-heading {
      margin-bottom: $grid-base;
    }

    &-downloads-copy {
      span {
        display: inline-block;
      }
      span:first-child {
        font-weight: 700;
        margin-bottom: 0.5 * $grid-base;
      }

      &-wrapper {
        :deep(.get-app) {
          justify-content: flex-start;
        }

        &--desktop {
          display: flex;
          flex-direction: row-reverse;
          justify-content: flex-end;
          gap: 2 * $grid-base;

          @include mq($mq-md) {
            gap: 3 * $grid-base;
          }
        }
      }
    }

    &-content {
      grid-area: content;

      @include mq($mq-md) {
        max-width: 535px;
      }
    }

    &-animation {
      grid-area: animation;
      max-width: 718px;

      @include mq($mq-sm) {
        display: flex;
        flex-direction: column-reverse;
        align-items: flex-end;
      }

      @include mq($mq-md) {
        flex-direction: row-reverse;
      }
    }

    &-lottie {
      margin: 2 * $grid-base auto;
      max-width: 465px;
    }

    &-link {
      grid-area: link;
      width: fit-content;
    }

    &-link-new-customer {
      grid-area: new-link;
      width: fit-content;
    }

    &-awards {
      display: flex;
      gap: $grid-base;

      @include mq($mq-md) {
        flex-direction: column-reverse;
        margin-bottom: 4 * $grid-base;
        min-width: 95px;
      }
    }
  }

  &__footer-banner {
    margin-bottom: 12 * $grid-base;

    :deep(.banner-teaser__container) {
      .grid-row .grid-column {
        padding-inline: 0;

        @include mq($mq-xl) {
          padding-left: 3 * $grid-base;
        }
      }
    }

    :deep(.image-teaser__background-image) {
      img {
        aspect-ratio: 32 / 21;

        @include mq($mq-sm) {
          aspect-ratio: 482 / 109;
          object-position: 70%;
        }

        @include mq($mq-md) {
          aspect-ratio: 1280 / 289;
        }
      }
    }

    :deep(.image-teaser__image) {
      align-content: center;
      margin-top: $grid-base * 2;
      margin-bottom: 44px;

      @include mq($mq-sm) {
        margin-bottom: 54px;
      }

      @include mq($mq-md) {
        height: auto;
        align-self: end;
        margin-bottom: 100px;
      }
    }

    :deep(.image-teaser__button) {
      @include mq(false, $mq-md - 1) {
        text-align: center;
      }
    }
  }

  @include mq($mq-md) {
    :deep(.banner-teaser--v-align-start) {
      align-items: center;
    }
  }

  &__flyout-banner {
    align-items: center;
    background-color: var(--color-primary);
    border-radius: 6px 6px 0 0;
    bottom: 0;
    box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
    display: flex;
    justify-content: space-between;
    left: 0;
    margin: 0 auto 2 * $grid-base;
    max-width: 726px;
    padding: $grid-base 2 * $grid-base;
    position: sticky;
    right: 0;
    transform: translateY(200px);
    z-index: 2;

    @include mq(727px) {
      border-radius: 6px;
    }

    &-text {
      margin-bottom: 0;
      max-width: 50%;

      @include mq(850px) {
        max-width: unset;
        margin-left: 74px;
      }

      &--mobile {
        @include mq(850px) {
          display: none;
        }
      }

      &--desktop {
        @include mq(false, 849px) {
          display: none;
        }
      }
    }

    &-app-screen {
      bottom: 0;
      left: 2 * $grid-base;
      position: absolute;

      @include mq(false, 849px) {
        display: none;
      }
    }

    &-qr {
      :deep(img) {
        border: 4px solid var(--color-primary);
        border-radius: 4px;
        bottom: 0.5rem;
        position: absolute;
        right: 1.5rem;
      }
    }

    :deep(.get-app) {
      margin-top: 0;
    }
  }
}
</style>
